import React, { useState } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Avatar,
  Slide,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { WhatsApp, Close, Send } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const WhatsAppButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSendMessage = () => {
    const whatsappNumber = '916309561983';
    const encodedMessage = encodeURIComponent(
      `Hi, I'm ${name || 'a customer'}. ${message || 'I\'m interested in your sofas.'}`
    );
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    handleClose();
    setMessage('');
    setName('');
  };

  const quickMessages = [
    "I'm interested in your sofa collection",
    "Can you help me choose the right sofa?",
    "What are your current offers?",
    "I need a custom sofa quote",
  ];

  return (
    <>
      {/* Floating WhatsApp Button */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
        }}
      >
        <Fab
          color="success"
          aria-label="whatsapp"
          onClick={handleClickOpen}
          sx={{
            width: 60,
            height: 60,
            backgroundColor: '#25D366',
            '&:hover': {
              backgroundColor: '#128C7E',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
          }}
        >
          <WhatsApp sx={{ fontSize: 30 }} />
        </Fab>
      </motion.div>

      {/* WhatsApp Chat Dialog */}
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#25D366',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                backgroundColor: 'white',
                color: '#25D366',
                mr: 2,
                width: 40,
                height: 40,
              }}
            >
              <WhatsApp />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                AHMED SHAREEF
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Sofa makers
              </Typography>
            </Box>
          </Box>
          <Button
            onClick={handleClose}
            sx={{ color: 'white', minWidth: 'auto', p: 1 }}
          >
            <Close />
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              👋 Hi there! How can we help you today?
            </Typography>
            
            <TextField
              fullWidth
              label="Your Name"
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Your Message"
              multiline
              rows={4}
              variant="outlined"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Quick Messages:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {quickMessages.map((quickMsg, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  onClick={() => setMessage(quickMsg)}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    textTransform: 'none',
                    borderColor: 'grey.300',
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'grey.50',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  {quickMsg}
                </Button>
              ))}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleClose}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            startIcon={<Send />}
            sx={{
              backgroundColor: '#25D366',
              '&:hover': {
                backgroundColor: '#128C7E',
              },
              textTransform: 'none',
              px: 3,
            }}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pulsing Animation for Attention */}
      <style>
        {`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(37, 211, 102, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
            }
          }
          
          .MuiFab-root {
            animation: pulse 2s infinite;
          }
        `}
      </style>
    </>
  );
};

export default WhatsAppButton;